import type { ConfiguredAddOn, ConfiguredService } from '@/features/service-plans';
import type { DateKey } from '../utils/dateKey';
import { toDueBasketItems } from '../utils/dueBasketItems';
import { toMinorUnits } from '../utils/money';

/** Earliest due date across positively-priced dated items in both lists, or `null` when there
 *  are none. Inclusion rules mirror `latestPricedDueDate`/`buildCollectionTargets`. */
export const earliestPricedDueDate = (
    services: ConfiguredService[],
    addOns: ConfiguredAddOn[],
): DateKey | null => {
    const items = toDueBasketItems(services, addOns);

    let earliest: DateKey | null = null;
    for (const item of items) {
        if (!(toMinorUnits(item.grossPrice) > 0)) {
            continue;
        }
        const dateKey = item.dueDate;
        if (dateKey === null) {
            continue;
        }
        if (earliest === null || dateKey < earliest) {
            earliest = dateKey;
        }
    }
    return earliest;
};

/** Strictly before the anchor: a service due today or later is fine, and a first collection
 *  falling after a near-future service is the item-based design, deliberately not gated here. */
export const isPastDueForScheduling = (options: {
    earliestDue: DateKey | null;
    anchor: DateKey;
}): boolean => {
    const { earliestDue, anchor } = options;
    return earliestDue !== null && earliestDue < anchor;
};

/** Shown on re-open when a stale quote carries a priced service already in the past. */
export const PAST_SERVICE_DATE_WARNING =
    'This quote has a service scheduled in the past. Update the service dates before taking payment.';
